import { useEffect, useMemo } from 'react';
import { Spin } from 'antd';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { LoadingOutlined } from '@ant-design/icons';

import { ticketById as ticketByIdAction,
  ticketByIdClear as ticketByIdClearAction,
  TicketByIdGetParams } from '../../../../store/ticket/byId';
import { Action, useAuth } from '../../../../store/auth';
import { useLayoutContext } from '../../../Layout/context';
import { useTicket } from '../../../../store/ticket';
import ContentHeader from '../../../Common/ContentHeader';
import BadgeTicketStatus from '../../../Common/BadgeTicketStatus';
import { displayDate } from '../../../../utils/date';
import useTicketActions from '../hooks/useTicketActions';
import TicketActions from '../Actions';

import styles from './index.module.scss';

interface TicketViewContentProps {
  ticketById: (payload: TicketByIdGetParams) => Action<TicketByIdGetParams>;
  ticketByIdClear: () => Action;
}

function TicketViewContent({
  ticketById,
  ticketByIdClear,
}: TicketViewContentProps): JSX.Element {
  const { user } = useAuth();
  const { ticketId = '' } = useParams();
  const navigate = useNavigate();

  const { setBreadcrumbs } = useLayoutContext();

  const { byId } = useTicket();
  const {
    contextHolder,
    confirmDelete,
    confirmResolve,
    confirmOpen,
    update,
    deleteTicket,
  } = useTicketActions({
    onDeleted: () => navigate('/tickets'),
  });

  const responseUser = useMemo(() => byId.response?.user, [byId.response]);

  useEffect(() => {
    setBreadcrumbs([{ title: 'Ticket List', href: '/tickets' }, { title: 'Ticket View' }]);
  }, []);

  useEffect(() => {
    if (ticketId) {
      ticketById({ id: ticketId });
    }
  }, [ticketId, update.response]);

  useEffect(
    () => () => {
      ticketByIdClear();
    },
    [],
  );

  return (
    <>
      <ContentHeader title={`Ticket ${byId.response?.ticketId}`}>
        {(user?.role === 'root' || user?.role === 'admin') && ticketId && (
          <div className={styles.actions}>
            <TicketActions
              ticketId={ticketId}
              status={byId.response?.status}
              updateLoading={update.loading}
              deleteLoading={deleteTicket.loading}
              onResolve={confirmResolve}
              onOpen={confirmOpen}
              onDelete={confirmDelete}
            />
          </div>
        )}

        {contextHolder}
      </ContentHeader>

      <Spin spinning={byId.loading} indicator={<LoadingOutlined spin />}>
        <div className={styles.content}>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Ticket No.</div>
            <div className={styles.rowContent}>{byId.response?.ticketId}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Type</div>
            <div className={styles.rowContent}>{byId.response?.type}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Status</div>
            <div className={styles.rowContent}>
              <BadgeTicketStatus status={byId.response?.status} />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Date added</div>
            <div className={styles.rowContent}>{displayDate(byId.response?.createdAt)}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Reporter</div>
            <div className={styles.rowContent}>
              {responseUser ? <NavLink to={`/users/${responseUser?.id}`}>{responseUser?.email}</NavLink> : '-'}
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Description</div>
            <div className={styles.rowContent}>{byId.response?.description}</div>
          </div>
        </div>
      </Spin>
    </>
  );
}

export default connect(undefined, {
  ticketById: ticketByIdAction,
  ticketByIdClear: ticketByIdClearAction,
})(TicketViewContent);
